import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import useModalA11y from '../../../hooks/useModalA11y';
import { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import { OWNERSHIP_BASES, OWNERSHIP_STRUCTURES } from '../../../utils/ownershipCriteria';

/**
 * The explanation behind the info icon next to the "Ownership" label. Looks like the
 * search's "How to use the searches?" popup (same classes), but is a native <dialog> through
 * useModalA11y, so it has Escape, focus trap and focus return.
 */
const OwnershipInfoDialog = ({ onClose }) => {
  const dialogRef = useRef(null);
  useModalA11y({ isOpen: true, onClose, dialogRef });

  // Portalled to <body>: the trigger sits inside a field label, whose text styles would apply.
  return createPortal(
    <dialog
      ref={dialogRef}
      className="ownership-info-dialog"
      aria-labelledby="ownership-info-title"
    >
      <div className="how-to-profiler-search-popup">
        <div className="header">
          <h2 id="ownership-info-title" className="title">
            How do the ownership filters work?
          </h2>
          <button
            type="button"
            className="ownership-info-close"
            aria-label="Close"
            onClick={() => onClose()}
          >
            <img src={ICON_BUTTON_ICONS.X_LG} alt="" />
          </button>
        </div>
        <div className="text-content">
          <div className="row">
            Three filters narrow your search by who owns a company. They are applied together with
            your other criteria when you run the search.
          </div>
          <div className="row">
            <span className="highlight-how-to-profiler-search">Ownership</span>: whether the
            company&apos;s shares are traded on a stock exchange (
            <span className="highlight-how-to-profiler-search">Publicly listed</span>) or not (
            <span className="highlight-how-to-profiler-search">Not publicly listed</span>).
          </div>
          <div className="row">
            <span className="highlight-how-to-profiler-search">Ownership structure</span>: how the
            shares are spread across the shareholders. Several structures match any of them.
            <ul className="ownership-info-list">
              {OWNERSHIP_STRUCTURES.map((structure) => (
                <li key={structure.value}>
                  <span className="highlight-how-to-profiler-search">{structure.name}</span>:{' '}
                  {structure.rule}
                </li>
              ))}
            </ul>
          </div>
          <div className="row">
            <span className="highlight-how-to-profiler-search">Ownership chain position</span>:
            which shareholders are counted. Required as soon as you choose an ownership structure.
            <ul className="ownership-info-list">
              {OWNERSHIP_BASES.map((basis) => (
                <li key={basis.value}>
                  <span className="highlight-how-to-profiler-search">{basis.name}</span>:{' '}
                  {basis.explanation}
                </li>
              ))}
            </ul>
          </div>
          <div className="row hint-how-to-profiler-search">
            Example: Beispiel GmbH is 100% owned by Muster Holding GmbH, which Anna (60%) and Jonas
            (40%) own. Counted as direct shareholders, it is wholly owned; counted as ultimate
            shareholders, it is majority owned.
          </div>
          <div className="row hint-how-to-profiler-search">
            Hint: Highly fragmented and Fragmented only include companies whose shareholdings are
            fully disclosed. Individuals only leaves out companies owned by a fund or a group.
          </div>
          <div className="row hint-how-to-profiler-search">
            Shareholdings of publicly listed companies are rarely disclosed, so together with an
            ownership structure they return few listed companies. With Individuals only, Joint
            venture means two people who each hold about half.
          </div>
        </div>
      </div>
    </dialog>,
    document.body
  );
};

OwnershipInfoDialog.propTypes = {
  onClose: PropTypes.func.isRequired
};

/**
 * The info icon itself, in front of the "Ownership" label (MultiSelect `labelAddon`), sized and
 * placed like the label info icons of Industry, Value chain or CAGR (`.info-icon-wrap`). Those
 * show a hover tooltip; this one opens the explanation, so it is a button.
 */
const OwnershipInfoButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="info-icon-wrap ownership-info-trigger"
        aria-label="How the ownership filters work"
        aria-haspopup="dialog"
        onClick={(event) => {
          // The icon sits inside the field label; keep the click away from the dropdown.
          event.preventDefault();
          event.stopPropagation();
          setIsOpen(true);
        }}
      >
        <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} alt="" />
      </button>
      {isOpen && <OwnershipInfoDialog onClose={() => setIsOpen(false)} />}
    </>
  );
};

export default OwnershipInfoButton;
